/**
 * DocumentAgentLG
 *
 * Main page component for TipTap-based document agent.
 * Replicates DocumentAgent/PatentDraftAgent architecture with PaneManager and MobileLayout.
 */

import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { Container } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import { useWorkproductContext } from "../../Context/WorkproductContext";
import { useLanguageContext } from "../../Context/LanguageContext";
import {
  DocumentContextProvider,
  useDocumentContext,
} from "./DocumentAgentLGContext";
import {
  DocumentType,
  DocumentComment,
  DocumentSnapshot,
} from "./DocumentAgentLGTypes";
import DocumentAgentLGChat, { AgentType } from "./DocumentAgentLGChat";
import DocumentTabContentTipTap from "./DocumentTabContentTipTap";
import PaneManager from "../DocumentAgent/Panes/PaneManager";
import { AddDocumentType } from "../DocumentAgent/Panes/TabBar";
import MobileLayout from "../DocumentAgent/MobileLayout";
import FileUploadModal from "../DocumentAgent/FileUploadModal";
import {
  getFileConfigForWorkproduct,
  workproductFileConfigs,
} from "../DocumentAgent/Tool/Upload/FileUploadConfig";
import {
  handleFileUpload,
  FileUploadCallbacks,
} from "../DocumentAgent/Tool/Upload/FileUploadUtils";
import { useAgentChat } from "../DocumentAgent/Hooks/useAgentChat";
import {
  importDocxToHtml,
  uploadDocxImagesToGCS,
  convertCommentMarkersToSpans,
} from "../../Components/TipTapEditor/utils/docxImport";
import {
  importDocxV2,
  uploadDocxImagesToGCSFromJson,
} from "../../Components/TipTapEditor/utils/docxImportV2";
import "../DocumentAgent/Panes/panes.css";
import "./CSS/DocumentAgentLG.css";
import "../Workspace/workspace.css";
import "../Workspace/editor-workspace.css";

// ============================================================================
// TYPES
// ============================================================================

interface TipTapAgentFileContent {
  documents?: Record<string, any>;
  currentChatId?: string;
  lastUsedChatId?: string;
  updatedAt?: string;
}

/** Override tab content rendering for specific documents (e.g., Figures panel). */
type RenderTabContentOverride = (
  docId: string,
  doc: DocumentSnapshot,
  defaultRender: () => React.ReactNode
) => React.ReactNode;

interface DocumentAgentLGInnerProps {
  readOnly?: boolean;
  /** Controls document panel visibility: "always" (default two-panel), "auto" (show when docs exist), "hidden" (chat only) */
  documentPanelMode?: "always" | "auto" | "hidden";
  /** When true, documents are view-only but chat remains active */
  documentReadOnly?: boolean;
  sharedChatHistory?: any[];
  sharedAvailableChats?: any[];
  sharedChatHistories?: Record<string, any[]>;
  /** Lets domain wrappers intercept tab rendering for specific documents */
  renderTabContentOverride?: RenderTabContentOverride;
  /** Pre-selects agent type (e.g. "patent-draft") */
  defaultAgentType?: AgentType;
  /** Initial chat mode: "ask" | "plan" | "edit". User can still switch. */
  defaultAgentMode?: "ask" | "plan" | "edit";
  /** Overrides workproduct.type for agent chat routing (e.g. "FTO Product Description Agent" when DB stores "FTO Search") */
  agentWorkproductType?: string;
  /** Domain-specific context (e.g. { patentOffice, patentType }) sent with each agent request */
  domainContext?: Record<string, any>;
  /** Pre-create form documents on mount (used by domain wrappers like FTO to ensure a form tab is always present) */
  initialFormDocuments?: Array<{
    docId: string;
    documentName: string;
    formData: Record<string, any>;
    meta?: Record<string, any>;
  }>;
  /** Called when the agent finishes a response (isAgentWorking transitions true→false) */
  onAgentComplete?: () => void;
  /** Initial chat/document panel split percentages [left, right]. Default [30, 70]. */
  initialPanelSplit?: [number, number];
  /** Callback to check if a tab can be removed. Return false to prevent removal. */
  canRemoveTab?: (docId: string) => boolean;
  /** Called after images are uploaded to GCS in chat. Used by FTO to add images to product panel. */
  onImagesUploaded?: (images: { gcsPath: string; proxyUrl: string }[]) => void;
  /** Form doc configs that should receive uploaded chat images (always provided, even when initialFormDocuments is undefined). */
  imageTargetFormDocuments?: Array<{
    docId: string;
    documentName: string;
    formData: Record<string, any>;
    meta?: Record<string, any>;
  }>;
  /** Replaces the default chat welcome message (passed through to the chat). */
  welcomeMessageOverride?: React.ReactNode;
  /** Extra action button(s) rendered with the chat input controls. Function form receives { hasUserMessages }. */
  extraInputActions?:
    | React.ReactNode
    | ((ctx: {
        hasUserMessages: boolean;
        initialRequest?: string;
        isBusy: boolean;
        hasUnsavedDocuments: boolean;
      }) => React.ReactNode);
  /** Rendered after the last chat message (e.g. the attorney-review offer). */
  messageFooter?:
    | React.ReactNode
    | ((ctx: {
        hasUserMessages: boolean;
        initialRequest?: string;
        isBusy: boolean;
        hasUnsavedDocuments: boolean;
      }) => React.ReactNode);
  /** Return false to skip hydrating a stored document into the panel (e.g. hide legacy form docs). */
  hydrateDocumentFilter?: (docId: string, meta: any) => boolean;
  /** Hide just the chat share-report button (lighter than minimalChrome). */
  hideShareButton?: boolean;
  /** Hide just the Ask/Plan/Execute mode selector; the agent stays in defaultAgentMode. */
  hideModeSelector?: boolean;
  consumerMode?: boolean;
  initialMessage?: string;
  onInitialMessageConsumed?: () => void;
}

// ============================================================================
// INNER COMPONENT (has access to DocumentContext)
// ============================================================================

const DocumentAgentLGInner: React.FC<DocumentAgentLGInnerProps> = ({
  readOnly = false,
  documentPanelMode = "always",
  documentReadOnly = false,
  sharedChatHistory,
  sharedAvailableChats,
  sharedChatHistories,
  renderTabContentOverride,
  defaultAgentType,
  defaultAgentMode,
  agentWorkproductType,
  domainContext,
  initialFormDocuments,
  onAgentComplete,
  initialPanelSplit,
  canRemoveTab,
  onImagesUploaded,
  imageTargetFormDocuments,
  welcomeMessageOverride,
  extraInputActions,
  messageFooter,
  hydrateDocumentFilter,
  hideShareButton,
  hideModeSelector,
  consumerMode,
  initialMessage,
  onInitialMessageConsumed,
}) => {
  const { t, i18n } = useTranslation();
  const { currentLanguage } = useLanguageContext();
  const {
    workproduct,
    fileContent,
    readOnly: contextReadOnly,
  } = useWorkproductContext();
  const isReadOnly = readOnly || contextReadOnly;
  const isDocumentReadOnly = documentReadOnly || isReadOnly;

  const {
    addTipTapDocument,
    addPDFDocument,
    addFormDocument,
    getDocumentById,
    documentContext,
    setSelectedDocument,
    setVisibleDocuments,
    markDocumentClean,
    processPDFUpload,
    renameDocument,
    removeDocument,
    isAgentWorking,
    updateFormData,
  } = useDocumentContext();

  // When images are uploaded in chat, append them to form_mutable docs that have productImages.
  // Uses imageTargetFormDocuments (always defined) so this works even before the form tab exists.
  const handleImagesUploadedInternal = useCallback(
    (images: { gcsPath: string; proxyUrl: string }[]) => {
      const targets = imageTargetFormDocuments || initialFormDocuments;
      if (targets) {
        const newImages = images.map(img => ({
          gcsPath: img.gcsPath,
          description: "",
        }));
        for (const form of targets) {
          const existing = getDocumentById(form.docId) as any;
          if (existing?.documentType === "form_mutable" && existing.formData) {
            // Append to existing form doc
            const currentImages = existing.formData.productImages || [];
            updateFormData(form.docId, {
              ...existing.formData,
              productImages: [...currentImages, ...newImages],
            });
          } else if (!existing) {
            // Create the form doc with the uploaded images
            const formData = { ...form.formData, productImages: newImages };
            addFormDocument(
              form.docId,
              form.documentName,
              formData,
              true,
              undefined,
              undefined,
              false,
              form.meta
            );
            // Auto-select the newly created form tab
            setSelectedDocument(form.docId);
          }
        }
      }
      // Also call parent callback (e.g., FTO persists to backend)
      if (onImagesUploaded) onImagesUploaded(images);
    },
    [
      imageTargetFormDocuments,
      initialFormDocuments,
      getDocumentById,
      updateFormData,
      addFormDocument,
      onImagesUploaded,
      setSelectedDocument,
    ]
  );

  // Fire onAgentComplete callback when agent finishes a response
  const prevAgentWorkingRef = useRef(false);
  useEffect(() => {
    if (prevAgentWorkingRef.current && !isAgentWorking && onAgentComplete) {
      onAgentComplete();
    }
    prevAgentWorkingRef.current = isAgentWorking;
  }, [isAgentWorking, onAgentComplete]);

  const workproductType =
    agentWorkproductType || workproduct?.type || "TipTap Document Agent";
  const backendUrl =
    process.env.REACT_APP_BACKEND_URL || "http://localhost:3001";

  // Language normalization
  const effectiveLanguage = useMemo(() => {
    const lang = i18n.language || "en";
    if (lang.toLowerCase().startsWith("zh") || currentLanguage === "中文") {
      return "zh";
    }
    return "en";
  }, [i18n.language, currentLanguage]);

  // Parsed file content
  const [parsedFileContent, setParsedFileContent] =
    useState<TipTapAgentFileContent | null>(null);

  // Track which fileContent has already been hydrated so the hydration effect
  // does not re-add documents that were explicitly deleted by the user.
  const hydratedFileContentRef = useRef<string | null>(null);
  const parsedFileContentSourceRef = useRef<string | null>(null);

  // Panel sizing
  const [leftPanelWidth, setLeftPanelWidth] = useState(
    initialPanelSplit?.[0] ?? 30
  );
  const [rightPanelWidth, setRightPanelWidth] = useState(
    initialPanelSplit?.[1] ?? 70
  );
  const containerRef = useRef<HTMLDivElement>(null);

  // Tab management
  const [activeRightTab, setActiveRightTab] = useState<string>("");
  // Tracks the document ids seen on the previous render so the active-tab
  // effect can tell a newly-added document apart from initial hydration.
  const prevDocIdsRef = useRef<string[]>([]);

  // Derive availableDocuments directly from context (no separate state).
  // This eliminates the race condition where a useEffect-based sync could
  // re-add a deleted document or delay showing a newly-added one.
  const availableDocuments = useMemo(
    () =>
      Object.entries(documentContext.documents).map(([docId, doc]) => ({
        docId,
        documentName: doc.documentName,
        documentType: doc.documentType as DocumentType,
      })),
    [documentContext.documents]
  );

  // Whether to show the document panel based on documentPanelMode
  const showDocumentPanel = useMemo(() => {
    if (documentPanelMode === "hidden") return false;
    if (documentPanelMode === "auto") return availableDocuments.length > 0;
    return true; // "always"
  }, [documentPanelMode, availableDocuments.length]);

  // Memoized array of tab IDs for PaneManager
  // This ensures PaneManager gets a stable reference when the list hasn't changed
  const allTabIds = useMemo(
    () => availableDocuments.map(d => d.docId),
    [availableDocuments]
  );

  // Resizer state
  const [isDragging, setIsDragging] = useState(false);
  const [activeResizer, setActiveResizer] = useState<"left" | null>(null);
  const activeResizerRef = useRef<"left" | null>(null);
  const panelWidthsRef = useRef({
    left: leftPanelWidth,
    right: rightPanelWidth,
  });

  // File upload modal
  const [showFileUploadModal, setShowFileUploadModal] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileConfig = useMemo(
    () => getFileConfigForWorkproduct(workproductType),
    [workproductType]
  );

  // Hidden file inputs for add document feature
  const pdfInputRef = useRef<HTMLInputElement>(null);
  const docxInputRef = useRef<HTMLInputElement>(null);
  const importFileInputRef = useRef<HTMLInputElement>(null);
  const [isAddingDocument, setIsAddingDocument] = useState(false);

  // ============================================================================
  // CHAT PERSISTENCE
  // ============================================================================

  const persistChatId = useCallback(
    async (newChatId: string | null) => {
      if (!workproduct) return;

      setParsedFileContent(current => {
        if (!current) return current;
        return {
          ...current,
          currentChatId: newChatId || undefined,
          lastUsedChatId: newChatId || current.lastUsedChatId,
          updatedAt: new Date().toISOString(),
        };
      });

      try {
        await fetch(
          `${backendUrl}/api/document/workproduct/${workproduct.id}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({
              workproductId: workproduct.id,
              updatedContent: {
                currentChatId: newChatId || undefined,
                lastUsedChatId: newChatId || parsedFileContent?.lastUsedChatId,
              },
            }),
          }
        );
      } catch (error) {
        console.error("Error saving chat ID change:", error);
      }
    },
    [backendUrl, workproduct, parsedFileContent?.lastUsedChatId]
  );

  // Chat hook — skip API calls in readOnly (shared) mode since chat data
  // comes from shared props, not from backend fetches.
  const {
    currentChatId,
    messages: chatMessages,
    availableChats,
    isConversationLoading,
    handleChatIdChange,
    handleNewMessage,
    fetchAvailableChats,
  } = useAgentChat(isReadOnly ? "" : workproduct?.id || "", workproductType, {
    onChatIdPersist: isReadOnly ? undefined : persistChatId,
    autoSelectLatest: !isReadOnly,
  });

  // State for read-only (shared) mode
  const parsedSharedChats = sharedAvailableChats || [];
  const [sharedCurrentChatId, setSharedCurrentChatId] = useState<
    string | undefined
  >(parsedSharedChats.length > 0 ? parsedSharedChats[0].chatId : undefined);

  // ============================================================================
  // RESIZER LOGIC
  // ============================================================================

  useEffect(() => {
    activeResizerRef.current = activeResizer;
    panelWidthsRef.current = { left: leftPanelWidth, right: rightPanelWidth };
  }, [activeResizer, leftPanelWidth, rightPanelWidth]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!containerRef.current || !activeResizerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const containerWidth = rect.width;
    const mouseX = e.clientX - rect.left;
    const mousePercentage = (mouseX / containerWidth) * 100;

    const newLeftWidth = Math.max(20, Math.min(80, mousePercentage));
    const newRightWidth = 100 - newLeftWidth;

    setLeftPanelWidth(newLeftWidth);
    setRightPanelWidth(newRightWidth);
  }, []);

  const stopDragging = useCallback(() => {
    setIsDragging(false);
    setActiveResizer(null);
    activeResizerRef.current = null;
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", stopDragging);
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
  }, [handleMouseMove]);

  const handleLeftResizerMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsDragging(true);
      setActiveResizer("left");
      activeResizerRef.current = "left";
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", stopDragging);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    },
    [handleMouseMove, stopDragging]
  );

  useEffect(() => {
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", stopDragging);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [handleMouseMove, stopDragging]);

  // ============================================================================
  // FILE CONTENT PARSING & DOCUMENT HYDRATION
  // ============================================================================

  useEffect(() => {
    if (fileContent) {
      const parsed = JSON.parse(fileContent) as TipTapAgentFileContent;
      // Callback/context changes may rerun hydration; retain chat metadata saved locally.
      if (parsedFileContentSourceRef.current !== fileContent) {
        parsedFileContentSourceRef.current = fileContent;
        setParsedFileContent(parsed);
      }

      // Restore chat ID
      const fileContentChatId = parsed.currentChatId;
      if (!currentChatId && fileContentChatId) {
        void handleChatIdChange(fileContentChatId);
      }

      // Hydrate documents — only when fileContent itself changes, not when
      // other dependencies (e.g. getDocumentById) trigger a re-run.  Without
      // this guard, removing a document causes getDocumentById to change,
      // which re-runs this effect, finds the deleted doc missing, and
      // re-adds it from fileContent — making the tab reappear.
      if (hydratedFileContentRef.current !== fileContent && parsed.documents) {
        hydratedFileContentRef.current = fileContent;
        Object.entries(parsed.documents).forEach(([docId, meta]) => {
          // Domain wrappers can exclude stored docs from the panel
          // (e.g. Legal Q&A v2 hides the legacy Outside Counsel form).
          if (hydrateDocumentFilter && !hydrateDocumentFilter(docId, meta)) {
            return;
          }
          const existing = getDocumentById(docId);
          if (existing) return;

          try {
            if (meta.documentType === "pdf") {
              addPDFDocument(
                docId,
                meta.documentName || "PDF Document",
                meta.pdfGcsPath || "",
                meta.ocrGcsPath || "",
                meta.ocrMetadata || {},
                meta.signedPdfUrl
              );
            } else if (meta.documentType === "tiptap") {
              addTipTapDocument(
                docId,
                meta.documentName || "Document",
                meta.content || { type: "doc", content: [] },
                meta.editable !== false,
                meta.gcsPath || "hydrated", // Signal this is a rehydration, not a new document
                meta.meta, // meta - pass through metadata from backend
                meta.htmlContent, // htmlContent - important for DOCX imports
                meta.version, // version from server metadata
                meta.comments // comments from server metadata
              );
              markDocumentClean(docId, meta.content);
            } else if (meta.documentType === "form_mutable") {
              let formDataToUse = meta.formData;
              if (!formDataToUse && meta.content) {
                try {
                  formDataToUse = JSON.parse(meta.content);
                } catch {}
              }
              addFormDocument(
                docId,
                meta.documentName || "Form",
                formDataToUse || {},
                true,
                undefined,
                undefined,
                false,
                meta.meta
              );
              // Auto-select newly created form documents so the user sees them immediately
              setSelectedDocument(docId);
            } else if (meta.documentType === "form_immutable") {
              addFormDocument(
                docId,
                meta.documentName || "Form",
                {},
                false,
                meta.gcsPath || "",
                meta.formSchema,
                true,
                meta.meta
              );
            }
          } catch (e) {
            console.error("Failed to hydrate document:", docId, e);
          }
        });
      }

      // Hydrate initial form documents from domain wrapper (e.g., FTO product description form)
      if (initialFormDocuments) {
        for (const form of initialFormDocuments) {
          const existing = getDocumentById(form.docId);
          if (!existing) {
            addFormDocument(
              form.docId,
              form.documentName,
              form.formData,
              true,
              undefined,
              undefined,
              false,
              form.meta
            );
          }
        }
      }
    }
  }, [
    fileContent,
    currentChatId,
    handleChatIdChange,
    addTipTapDocument,
    addPDFDocument,
    addFormDocument,
    getDocumentById,
    markDocumentClean,
    initialFormDocuments,
    hydrateDocumentFilter,
  ]);

  // ============================================================================
  // ACTIVE TAB MANAGEMENT
  // ============================================================================

  // Keep activeRightTab in sync as availableDocuments changes (derived via
  // useMemo, so it updates in the same render as the context change — no race
  // between deletion and re-sync). Three cases, in order:
  //   1. New document(s) appeared during the session → focus the newest one.
  //      This makes every creation path (agent SSE, fileContent re-hydration,
  //      import, patent add) auto-switch to the new tab uniformly.
  //   2. No documents left → clear the active tab.
  //   3. Active tab missing/removed → fall back to the first document.
  // Initial hydration (prevIds empty) is intentionally treated as "no new docs"
  // so the first render lands on the first tab rather than the last.
  useEffect(() => {
    const currentIds = availableDocuments.map(d => d.docId);
    const prevIds = prevDocIdsRef.current;
    prevDocIdsRef.current = currentIds;

    if (currentIds.length === 0) {
      if (activeRightTab) setActiveRightTab("");
      return;
    }

    const newIds =
      prevIds.length > 0 ? currentIds.filter(id => !prevIds.includes(id)) : [];
    if (newIds.length > 0) {
      setActiveRightTab(newIds[newIds.length - 1]);
      return;
    }

    if (!activeRightTab || !currentIds.includes(activeRightTab)) {
      setActiveRightTab(currentIds[0]);
    }
  }, [availableDocuments, activeRightTab]);

  // Sync context metadata
  useEffect(() => {
    if (activeRightTab) {
      setSelectedDocument(activeRightTab);
    } else {
      setSelectedDocument(null);
    }
  }, [activeRightTab, setSelectedDocument]);

  // ============================================================================
  // DOCUMENT ACTIVATION
  // ============================================================================

  const handleDocumentActivated = useCallback((docId: string) => {
    setTimeout(() => setActiveRightTab(docId), 300);
  }, []);

  const handleRenameTab = useCallback(
    (docId: string, newName: string) => {
      if (isDocumentReadOnly) return;
      renameDocument(docId, newName);
    },
    [isDocumentReadOnly, renameDocument]
  );

  const handleRemoveTab = useCallback(
    (docId: string) => {
      if (isDocumentReadOnly) return;
      if (canRemoveTab && !canRemoveTab(docId)) return;

      // Just remove the document - let the useEffect below handle tab switching
      // This prevents race conditions between manual tab switching and the effect
      removeDocument(docId).catch(err => {
        console.error("📄 Failed to remove document:", err);
      });
    },
    [isDocumentReadOnly, removeDocument, canRemoveTab]
  );

  // ============================================================================
  // DOCUMENT CREATION & IMPORT HANDLERS
  // ============================================================================

  // Handler for "New Document" menu item
  const handleNewDocument = useCallback(() => {
    if (isDocumentReadOnly) return;

    const docId = uuidv4();
    const docNumber = availableDocuments.length + 1;
    addTipTapDocument(
      docId,
      `Document ${docNumber}`,
      { type: "doc", content: [{ type: "paragraph" }] },
      true
    );
    setActiveRightTab(docId);
  }, [isDocumentReadOnly, availableDocuments.length, addTipTapDocument]);

  // Shared logic for importing DOCX as a new document (used by both plus button and in-editor import)
  const handleImportDocxAsNewDocument = useCallback(
    async (file: File) => {
      if (
        file.name.toLowerCase().endsWith(".doc") &&
        !file.name.toLowerCase().endsWith(".docx")
      ) {
        alert(
          `"${file.name}" is a legacy .doc file and is not supported. Please save it as .docx and try again.`
        );
        return;
      }
      setIsAddingDocument(true);
      try {
        // Feature flag: use custom OOXML parser (V2) or mammoth (V1)
        // V2 reads raw OOXML directly — no marker injection or HTML round-trips.
        // V1 (mammoth) path kept as fallback; set to false to revert.
        const USE_CUSTOM_PARSER = true;

        const docId = uuidv4();
        const docName =
          file.name.replace(/\.docx$/i, "") || "Imported Document";

        if (USE_CUSTOM_PARSER) {
          // V2: Custom OOXML-to-ProseMirror parser (direct JSON, no HTML)
          const result = await importDocxV2(file);

          if (result.messages.length > 0) {
          }

          // Map comments to DocumentComment format
          const importedComments: DocumentComment[] = result.comments.map(
            c => ({
              id: c.id,
              content: c.content,
              author: c.author,
              createdAt: c.date || new Date().toISOString(),
            })
          );

          // Upload base64 images to GCS (mutates result.doc in place)
          try {
            await uploadDocxImagesToGCSFromJson(
              result.doc,
              docId,
              backendUrl,
              workproduct.id
            );
          } catch (imgErr) {}

          addTipTapDocument(
            docId,
            docName,
            result.doc, // ProseMirror JSON with all marks and attrs
            true,
            undefined, // gcsPath
            {
              // meta
              trackChanges: result.trackChanges,
              hasTrackChanges: result.hasTrackChanges,
              // Imported page header/footer + page setup — the pane reads
              // these on mount and persists them via document meta on save
              ...(result.headerFooter
                ? { headerFooter: result.headerFooter }
                : {}),
              ...(result.pageSetup ? { pageSetup: result.pageSetup } : {}),
            },
            undefined, // htmlContent — not needed for V2 (JSON has everything)
            undefined, // version
            importedComments
          );

          // Save original DOCX to GCS (fire-and-forget, non-blocking)
          try {
            const arrayBuf = await file.arrayBuffer();
            const base64 = btoa(
              new Uint8Array(arrayBuf).reduce(
                (data, byte) => data + String.fromCharCode(byte),
                ""
              )
            );
            fetch(`${backendUrl}/api/document-agent/save-original-docx`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              credentials: "include",
              body: JSON.stringify({
                workproductId: workproduct.id,
                docId,
                fileName: file.name,
                fileBase64: base64,
              }),
            })
              .then(resp => {})
              .catch(err => {});

            // (Removed 2026-07-11) The imported DOCX is no longer copied into a
            // shared parser-test corpus — that leaked customer documents
            // cross-tenant. See server.ts corpus-endpoint removal note.
          } catch (saveErr) {}
        } else {
          // V1: Mammoth-based pipeline (HTML output)
          // Import DOCX and convert to HTML.
          // Defer comment conversion so Unicode markers survive the
          // uploadDocxImagesToGCS DOM round-trip (DOMParser → innerHTML
          // restructures <span> elements that cross formatting boundaries).
          const result = await importDocxToHtml(file, {
            deferCommentConversion: true,
          });

          // Upload any embedded base64 images to GCS and replace with
          // signed URLs so the PostgreSQL payload stays small.
          // Unicode comment markers (\uE000…) survive as plain text.
          let finalHtml = result.html;
          try {
            finalHtml = await uploadDocxImagesToGCS(
              result.html,
              docId,
              backendUrl,
              workproduct.id
            );
          } catch (imgErr) {}

          // NOW convert comment markers to spans — after all DOM round-trips.
          let importedComments: DocumentComment[] = [];
          if (result.commentMarkerMeta && result.commentMarkerMeta.size > 0) {
            // Debug: check if Unicode markers survived the image upload DOM round-trip
            let markerCount = 0;
            for (let i = 0; i < finalHtml.length; i++) {
              if (finalHtml.charCodeAt(i) === 0xe000) markerCount++;
            }

            const converted = convertCommentMarkersToSpans(
              finalHtml,
              result.commentMarkerMeta
            );
            finalHtml = converted.html;

            importedComments = converted.comments.map(c => ({
              id: c.id,
              content: c.content,
              author: c.author,
              createdAt: c.date || new Date().toISOString(),
            }));
          }

          addTipTapDocument(
            docId,
            docName,
            { type: "doc", content: [{ type: "paragraph" }] },
            true,
            undefined, // gcsPath
            {
              // meta
              trackChanges: result.trackChanges,
              hasTrackChanges: result.hasTrackChanges,
            },
            finalHtml, // htmlContent (images now reference GCS URLs)
            undefined, // version
            importedComments // comments (9th parameter)
          );
        }

        setActiveRightTab(docId);
      } catch (error) {
        console.error("Error importing DOCX:", error);
        throw error; // Re-throw so caller can handle
      } finally {
        setIsAddingDocument(false);
      }
    },
    [addTipTapDocument, backendUrl, workproduct.id]
  );

  // Shared logic for importing PDF as a new document (used by both plus button and in-editor import)
  const handleImportPdfAsNewDocument = useCallback(
    async (file: File) => {
      setIsAddingDocument(true);
      try {
        const docId = await processPDFUpload(file, file.name);
        // Switch to the new tab immediately - the tab content will show
        // a loading state until the document context is fully updated
        setActiveRightTab(docId);
      } catch (error) {
        console.error("Error importing PDF:", error);
        throw error; // Re-throw so caller can handle
      } finally {
        setIsAddingDocument(false);
      }
    },
    [processPDFUpload]
  );

  // ============================================================================
  // TAB CONTENT RENDERING
  // ============================================================================

  const renderTabContent = useCallback(
    (docId: string, documentName: string) => {
      if (!workproduct) {
        return (
          <div className="tiptap-agent-tab-content">
            {t("documentAgent.loadingWorkproduct")}
          </div>
        );
      }

      const defaultRender = () => (
        <DocumentTabContentTipTap
          workproductId={workproduct.id}
          workproductType={workproductType}
          docId={docId}
          documentName={documentName}
          onNewDocument={handleNewDocument}
          onImportDocxAsNewTab={handleImportDocxAsNewDocument}
          onImportPdfAsNewTab={handleImportPdfAsNewDocument}
        />
      );

      // Allow domain wrappers to intercept rendering for specific documents
      if (renderTabContentOverride) {
        const doc = getDocumentById(docId);
        if (doc) {
          return renderTabContentOverride(docId, doc, defaultRender);
        }
      }

      return defaultRender();
    },
    [
      workproduct,
      workproductType,
      handleNewDocument,
      handleImportDocxAsNewDocument,
      handleImportPdfAsNewDocument,
      renderTabContentOverride,
      getDocumentById,
      t,
    ]
  );

  // Function to check if a tab has unsaved changes
  const getTabUnsaved = useCallback(
    (tabId: string): boolean => {
      const doc = documentContext.documents[tabId];
      if (!doc) return false;

      // Check if document is dirty based on document type
      if (
        doc.documentType === "tiptap" ||
        doc.documentType === "form_mutable"
      ) {
        return doc.isDirty === true;
      }

      return false;
    },
    [documentContext.documents]
  );

  // ============================================================================
  // FILE UPLOAD
  // ============================================================================

  const fileUploadCallbacks: FileUploadCallbacks = {
    onMessage: () => {},
    addPDFDocument,
    addFormDocument,
    updateDocumentContent: (docId, content, documentName) => {
      // For TipTap, we need to convert HTML to JSON if needed
      addTipTapDocument(
        docId,
        documentName || "Document",
        { type: "doc", content: [] },
        true
      );
    },
    onDocumentAdded: (docId, _documentName) => {
      setActiveRightTab(docId);
    },
  };

  const handleFileUploadFromTabs = async (files: File[], fileType: string) => {
    if (!workproduct) return;
    setIsUploading(true);
    try {
      await handleFileUpload(
        files,
        fileType,
        workproduct.id,
        workproductType,
        backendUrl,
        workproductFileConfigs,
        fileUploadCallbacks,
        currentChatId || undefined,
        effectiveLanguage
      );
      setShowFileUploadModal(false);
    } catch (e) {
      console.error("Error uploading files:", e);
    } finally {
      setIsUploading(false);
    }
  };

  // ============================================================================
  // ADD DOCUMENT HANDLERS
  // ============================================================================

  const handleAddDocument = useCallback(
    (type: AddDocumentType) => {
      if (isReadOnly) return;

      if (type === "blank") {
        const docId = uuidv4();
        const docNumber = availableDocuments.length + 1;
        addTipTapDocument(
          docId,
          `Document ${docNumber}`,
          { type: "doc", content: [{ type: "paragraph" }] },
          true
        );
        setActiveRightTab(docId);
      } else if (type === "pdf") {
        pdfInputRef.current?.click();
      } else if (type === "docx") {
        docxInputRef.current?.click();
      }
    },
    [isReadOnly, availableDocuments.length, addTipTapDocument]
  );

  const handlePdfFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      try {
        await handleImportPdfAsNewDocument(file);
      } finally {
        // Reset input so same file can be selected again
        if (pdfInputRef.current) {
          pdfInputRef.current.value = "";
        }
      }
    },
    [handleImportPdfAsNewDocument]
  );

  const handleDocxFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      try {
        await handleImportDocxAsNewDocument(file);
      } finally {
        // Reset input so same file can be selected again
        if (docxInputRef.current) {
          docxInputRef.current.value = "";
        }
      }
    },
    [handleImportDocxAsNewDocument]
  );

  // Unified import handler — detects file type from extension
  const handleImportFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      try {
        const ext = file.name.split(".").pop()?.toLowerCase();
        if (ext === "pdf") {
          await handleImportPdfAsNewDocument(file);
        } else if (ext === "docx") {
          await handleImportDocxAsNewDocument(file);
        } else if (ext === "doc") {
          alert(
            `"${file.name}" is a legacy .doc file and is not supported. Please save it as .docx and try again.`
          );
        }
      } finally {
        if (importFileInputRef.current) {
          importFileInputRef.current.value = "";
        }
      }
    },
    [handleImportPdfAsNewDocument, handleImportDocxAsNewDocument]
  );

  // ============================================================================
  // LOADING STATES
  // ============================================================================

  if (!workproduct || !fileContent) {
    return (
      <Container>
        <div className="alert alert-info">
          {t("documentAgent.loadingAgent")}
        </div>
      </Container>
    );
  }

  if (!workproduct?.id) {
    return (
      <div className="tiptap-document-agent workspace-theme">
        <div style={{ padding: "20px", textAlign: "center" }}>
          <p>{t("documentAgent.loadingWorkproduct")}</p>
        </div>
      </div>
    );
  }

  // ============================================================================
  // RENDER PANELS
  // ============================================================================

  const displayAvailableChats = isReadOnly ? parsedSharedChats : availableChats;
  const displayChatId = isReadOnly ? sharedCurrentChatId : currentChatId;
  const displayMessages = isReadOnly
    ? sharedChatHistories && sharedCurrentChatId
      ? sharedChatHistories[sharedCurrentChatId]
      : sharedChatHistory || []
    : chatMessages;

  const chatPanel = (
    <DocumentAgentLGChat
      workproductId={workproduct.id}
      workproductType={workproductType}
      chatId={displayChatId || undefined}
      messages={displayMessages}
      isConversationLoading={isReadOnly ? false : isConversationLoading}
      availableChats={displayAvailableChats}
      onChatIdChange={
        isReadOnly
          ? id => setSharedCurrentChatId(id || undefined)
          : handleChatIdChange
      }
      onNewMessage={isReadOnly ? undefined : handleNewMessage}
      onRefreshChats={isReadOnly ? undefined : fetchAvailableChats}
      setActiveRightTab={setActiveRightTab}
      onDocumentActivated={handleDocumentActivated}
      inputDisabled={isReadOnly}
      readOnly={isReadOnly}
      onImportDocxAsNewDocument={
        isReadOnly ? undefined : handleImportDocxAsNewDocument
      }
      onImportPdfAsNewDocument={
        isReadOnly ? undefined : handleImportPdfAsNewDocument
      }
      defaultAgentType={defaultAgentType}
      defaultAgentMode={defaultAgentMode}
      domainContext={domainContext}
      onImagesUploaded={handleImagesUploadedInternal}
      welcomeMessageOverride={welcomeMessageOverride}
      extraInputActions={extraInputActions}
      messageFooter={messageFooter}
      hideShareButton={hideShareButton}
      hideModeSelector={hideModeSelector}
      consumerMode={consumerMode}
      initialMessage={initialMessage}
      onInitialMessageConsumed={onInitialMessageConsumed}
    />
  );

  const documentPanel = (
    <div className="tiptap-document-panel-wrapper">
      {/* Loading overlay for PDF upload/OCR processing */}
      {isAddingDocument && (
        <div className="tiptap-upload-overlay">
          <div className="tiptap-upload-overlay-content">
            <div className="tiptap-upload-spinner" />
            <div className="tiptap-upload-text">
              {t(
                "documentAgent.uploadingPdf",
                "Uploading and processing Document..."
              )}
            </div>
            <div className="tiptap-upload-subtext">
              {t(
                "documentAgent.ocrProcessing",
                "OCR processing may take a moment"
              )}
            </div>
          </div>
        </div>
      )}

      {availableDocuments.length > 0 ? (
        <PaneManager
          workproductId={workproduct.id}
          allTabIds={allTabIds}
          storageKey={`tiptap_panes_${workproduct.id}`}
          requestedActiveTabId={activeRightTab}
          renderTabContent={tabId => {
            const doc = availableDocuments.find(d => d.docId === tabId);
            if (!doc)
              return (
                <div style={{ padding: 12 }}>
                  {t("documentAgent.tabNotFound")}
                </div>
              );
            return renderTabContent(doc.docId, doc.documentName);
          }}
          getTabTitle={tabId => {
            const doc = availableDocuments.find(d => d.docId === tabId);
            if (!doc) return tabId;
            const translationKey = `documentNames.${doc.documentName}`;
            const translated = t(translationKey);
            return translated !== translationKey
              ? translated
              : doc.documentName;
          }}
          getTabUnsaved={getTabUnsaved}
          onVisibleChange={docIds => setVisibleDocuments(docIds)}
          onAddDocument={isDocumentReadOnly ? undefined : handleAddDocument}
          onRenameTab={isDocumentReadOnly ? undefined : handleRenameTab}
          onRemoveTab={isDocumentReadOnly ? undefined : handleRemoveTab}
        />
      ) : (
        <div className="tiptap-agent-empty-state">
          <div className="tiptap-empty-icon">
            <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#adb5bd"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
              <polyline points="14 2 14 8 20 8" />
            </svg>
          </div>
          <h5>{t("documentAgent.noDocuments", "No Documents Available")}</h5>
          <p>
            {t(
              "documentAgent.noDocumentsDescription",
              "Get started by creating a blank document or importing an existing file."
            )}
          </p>
          {!isDocumentReadOnly && (
            <div className="tiptap-empty-card-row">
              <button
                className="tiptap-empty-card"
                onClick={() => handleAddDocument("blank")}
                disabled={isAddingDocument}
              >
                <div className="tiptap-empty-card-icon">
                  <svg
                    width="28"
                    height="28"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="12" y1="11" x2="12" y2="17" />
                    <line x1="9" y1="14" x2="15" y2="14" />
                  </svg>
                </div>
                <span className="tiptap-empty-card-title">
                  {t("documentAgent.blankDocument", "Blank Document")}
                </span>
                <span className="tiptap-empty-card-desc">
                  {t(
                    "documentAgent.blankDocumentDesc",
                    "Start with an empty editor"
                  )}
                </span>
              </button>

              <button
                className="tiptap-empty-card"
                onClick={() => importFileInputRef.current?.click()}
                disabled={isAddingDocument}
              >
                <div className="tiptap-empty-card-icon">
                  <svg
                    width="28"
                    height="28"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                </div>
                <span className="tiptap-empty-card-title">
                  {t("documentAgent.importDocument", "Import Document")}
                </span>
                <span className="tiptap-empty-card-desc">
                  {t(
                    "documentAgent.importDocumentDesc",
                    "Upload a PDF or Word file"
                  )}
                </span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="tiptap-document-agent workspace-theme">
      <MobileLayout
        chatPanel={chatPanel}
        documentPanel={showDocumentPanel ? documentPanel : null}
        defaultTab="chat"
        hasDocuments={showDocumentPanel}
        documentCount={showDocumentPanel ? availableDocuments.length : 0}
      >
        <Container fluid className="tiptap-agent-layout-container">
          <div className="resizable-container" ref={containerRef}>
            {/* Left Panel - Chat */}
            <div
              className="resizable-panel tiptap-agent-panel-border left"
              style={{
                width: showDocumentPanel ? `${leftPanelWidth}%` : "100%",
                overflowX: "hidden",
                pointerEvents: isDragging ? "none" : undefined,
              }}
            >
              {chatPanel}
            </div>

            {/* Resizer - only when document panel visible */}
            {showDocumentPanel && (
              <div
                className="resizer"
                onMouseDown={handleLeftResizerMouseDown}
              />
            )}

            {/* Right Panel - Documents - only when visible */}
            {showDocumentPanel && (
              <div
                className="resizable-panel tiptap-agent-panel-border right"
                style={{
                  width: `${rightPanelWidth}%`,
                  pointerEvents: isDragging ? "none" : undefined,
                }}
              >
                {documentPanel}
              </div>
            )}
          </div>
        </Container>
      </MobileLayout>

      {/* File Upload Modal */}
      <FileUploadModal
        show={showFileUploadModal}
        onHide={() => setShowFileUploadModal(false)}
        onFileUpload={handleFileUploadFromTabs}
        workproductType={workproductType}
        fileConfig={fileConfig}
        isLoading={isUploading}
        existingDocuments={availableDocuments.map(d => ({
          docId: d.docId,
          documentName: d.documentName,
        }))}
      />

      {/* Hidden file inputs for add document feature */}
      <input
        ref={pdfInputRef}
        type="file"
        accept=".pdf"
        style={{ display: "none" }}
        onChange={handlePdfFileChange}
      />
      <input
        ref={docxInputRef}
        type="file"
        accept=".docx"
        style={{ display: "none" }}
        onChange={handleDocxFileChange}
      />
      <input
        ref={importFileInputRef}
        type="file"
        accept=".pdf,.docx"
        style={{ display: "none" }}
        onChange={handleImportFileChange}
      />
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

interface DocumentAgentLGProps {
  readOnly?: boolean;
  /** Controls document panel visibility: "always" (default two-panel), "auto" (show when docs exist), "hidden" (chat only) */
  documentPanelMode?: "always" | "auto" | "hidden";
  /** When true, documents are view-only but chat remains active */
  documentReadOnly?: boolean;
  sharedChatHistory?: any[];
  sharedAvailableChats?: any[];
  sharedChatHistories?: Record<string, any[]>;
  /** Lets domain wrappers intercept tab rendering for specific documents */
  renderTabContentOverride?: RenderTabContentOverride;
  /** Pre-selects agent type (e.g. "patent-draft") */
  defaultAgentType?: AgentType;
  /** Initial chat mode: "ask" | "plan" | "edit". User can still switch. */
  defaultAgentMode?: "ask" | "plan" | "edit";
  /** Overrides workproduct.type for agent chat routing (e.g. "FTO Product Description Agent" when DB stores "FTO Search") */
  agentWorkproductType?: string;
  /** Domain-specific context (e.g. { patentOffice, patentType }) sent with each agent request */
  domainContext?: Record<string, any>;
  /** Pre-create form documents on mount (used by domain wrappers like FTO to ensure a form tab is always present) */
  initialFormDocuments?: Array<{
    docId: string;
    documentName: string;
    formData: Record<string, any>;
    meta?: Record<string, any>;
  }>;
  /** Called when the agent finishes a response (isAgentWorking transitions true→false) */
  onAgentComplete?: () => void;
  /** Initial chat/document panel split percentages [left, right]. Default [30, 70]. */
  initialPanelSplit?: [number, number];
  /** Callback to check if a tab can be removed. Return false to prevent removal. */
  canRemoveTab?: (docId: string) => boolean;
  /** Called after images are uploaded to GCS in chat. Used by FTO to add images to product panel. */
  onImagesUploaded?: (images: { gcsPath: string; proxyUrl: string }[]) => void;
  /** Form doc configs that should receive uploaded chat images (always provided, even when initialFormDocuments is undefined). */
  imageTargetFormDocuments?: Array<{
    docId: string;
    documentName: string;
    formData: Record<string, any>;
    meta?: Record<string, any>;
  }>;
  /** Replaces the default chat welcome message (passed through to the chat). */
  welcomeMessageOverride?: React.ReactNode;
  /** Extra action button(s) rendered with the chat input controls. Function form receives { hasUserMessages }. */
  extraInputActions?:
    | React.ReactNode
    | ((ctx: {
        hasUserMessages: boolean;
        initialRequest?: string;
        isBusy: boolean;
        hasUnsavedDocuments: boolean;
      }) => React.ReactNode);
  /** Rendered after the last chat message (e.g. the attorney-review offer). */
  messageFooter?:
    | React.ReactNode
    | ((ctx: {
        hasUserMessages: boolean;
        initialRequest?: string;
        isBusy: boolean;
        hasUnsavedDocuments: boolean;
      }) => React.ReactNode);
  /** Return false to skip hydrating a stored document into the panel (e.g. hide legacy form docs). */
  hydrateDocumentFilter?: (docId: string, meta: any) => boolean;
  /** Hide just the chat share-report button (lighter than minimalChrome). */
  hideShareButton?: boolean;
  /** Hide just the Ask/Plan/Execute mode selector; the agent stays in defaultAgentMode. */
  hideModeSelector?: boolean;
  consumerMode?: boolean;
  initialMessage?: string;
  onInitialMessageConsumed?: () => void;
}

const DocumentAgentLG: React.FC<DocumentAgentLGProps> = ({
  readOnly = false,
  documentPanelMode = "always",
  documentReadOnly = false,
  sharedChatHistory,
  sharedAvailableChats,
  sharedChatHistories,
  renderTabContentOverride,
  defaultAgentType,
  defaultAgentMode,
  agentWorkproductType,
  domainContext,
  initialFormDocuments,
  onAgentComplete,
  initialPanelSplit,
  canRemoveTab,
  onImagesUploaded,
  imageTargetFormDocuments,
  welcomeMessageOverride,
  extraInputActions,
  messageFooter,
  hydrateDocumentFilter,
  hideShareButton,
  hideModeSelector,
  consumerMode,
  initialMessage,
  onInitialMessageConsumed,
}) => {
  const { t } = useTranslation();
  const { workproduct } = useWorkproductContext();

  if (!workproduct) {
    return <div>{t("documentAgent.loadingWorkproduct")}</div>;
  }

  if (!workproduct.id) {
    return <div>{t("documentAgent.invalidWorkproduct")}</div>;
  }

  return (
    <DocumentContextProvider
      workproductId={workproduct.id}
      workproductType={workproduct.type || "TipTap Document Agent"}
      documentReadOnly={documentReadOnly || readOnly}
    >
      <DocumentAgentLGInner
        readOnly={readOnly}
        documentPanelMode={documentPanelMode}
        documentReadOnly={documentReadOnly}
        sharedChatHistory={sharedChatHistory}
        sharedAvailableChats={sharedAvailableChats}
        sharedChatHistories={sharedChatHistories}
        renderTabContentOverride={renderTabContentOverride}
        defaultAgentType={defaultAgentType}
        defaultAgentMode={defaultAgentMode}
        agentWorkproductType={agentWorkproductType}
        domainContext={domainContext}
        initialFormDocuments={initialFormDocuments}
        onAgentComplete={onAgentComplete}
        initialPanelSplit={initialPanelSplit}
        canRemoveTab={canRemoveTab}
        onImagesUploaded={onImagesUploaded}
        imageTargetFormDocuments={imageTargetFormDocuments}
        welcomeMessageOverride={welcomeMessageOverride}
        extraInputActions={extraInputActions}
        messageFooter={messageFooter}
        hydrateDocumentFilter={hydrateDocumentFilter}
        hideShareButton={hideShareButton}
        hideModeSelector={hideModeSelector}
        consumerMode={consumerMode}
        initialMessage={initialMessage}
        onInitialMessageConsumed={onInitialMessageConsumed}
      />
    </DocumentContextProvider>
  );
};

export default DocumentAgentLG;
